import { useEffect, useState } from 'react'
import { Box, TextField, Button, useTheme, Typography, IconButton, Collapse } from '@mui/material'
import ChatIcon from '@mui/icons-material/Chat'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { ModalWrapper } from 'src/components/modal-wrapper'
import { useStyles } from './styles'
import { useTranslation } from 'react-i18next'
import { ESettingsPromptFields } from 'src/types'
import { highlightVariables } from 'src/common/utils'

import { getHighlightVariableForField } from '../settings.utils'
import { SettinsFieldDescription } from './settings-field-description.component'
import { EPromptsGlobalImageVariables, EPromptsGlobalVariables } from 'src/constants'
import { TextTag } from 'src/components/text-tag'

interface IProps {
  label: string
  text: string
  field: ESettingsPromptFields
  onSave: (text: string, field: ESettingsPromptFields) => void
  isLoading?: boolean
  isMobile?: boolean
  description?: string
  showGlobalVariables?: boolean
}

export const SettingsItem = ({
  label,
  text,
  field,
  onSave,
  isMobile = false,
  isLoading = false,
  showGlobalVariables = true,
  description = '',
}: IProps) => {
  const [t] = useTranslation()

  const [value, setValue] = useState(text)
  const [isEditing, setIsEditing] = useState(false)
  const [openCollapse, setOpenCollapse] = useState(false)

  const theme = useTheme()
  const classes = useStyles()

  useEffect(() => {
    setValue(text)
  }, [isLoading])

  const handleSave = () => {
    onSave(value, field)
    setIsEditing(false)
  }

  const handleClose = () => {
    setIsEditing(false)
  }

  const renderText = (textValue: string) => {
    const variableToHighlight = getHighlightVariableForField(field)
    return (
      <Box sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
        {variableToHighlight ? highlightVariables(textValue, variableToHighlight) : textValue}
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? '20px' : '10px' }}>
            {!isMobile && (
              <IconButton onClick={() => setOpenCollapse(!openCollapse)}>
                <ExpandMoreIcon
                  sx={{
                    transform: openCollapse ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s',
                  }}
                />
              </IconButton>
            )}
            {isMobile && (
              <ChatIcon
                sx={{
                  width: isMobile ? '40px' : '24px',
                  height: isMobile ? '40px' : '24px',
                  color: isMobile ? theme.palette.custom.primary : theme.palette.custom.black,
                }}
              />
            )}

            <Typography
              sx={{
                fontSize: isMobile ? '18px' : 'inherit',
                fontWeight: isMobile ? 700 : 'inherit',
              }}
            >
              {label}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsEditing(true)}
            disabled={isLoading}
          >
            {t('edit-button-text.v')}
          </Button>
        </Box>
        {!isMobile && (
          <Collapse in={openCollapse} sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'center',
                pt: '20px',
              }}
            >
              {renderText(value)}
            </Box>
          </Collapse>
        )}
      </Box>
      <ModalWrapper className={classes.modalWrapper} open={isEditing} onClose={handleClose}>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            gap: '20px',
            width: '100%',
            flexDirection: 'column',
            justifyContent: 'space-between',
            pt: '30px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={isLoading}
              multiline
              maxRows={12}
              minRows={6}
            />
            {description && <SettinsFieldDescription field={field} description={description} />}

            {showGlobalVariables && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      px: '8px',
                      maxWidth: '400px',
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Global variables:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    {Object.values(EPromptsGlobalVariables).map((variable) => (
                      <TextTag
                        key={variable}
                        label={variable}
                        bgColor={theme.palette.custom.lightGreen}
                        color={theme.palette.text.secondary}
                        fontSize="12px"
                        size="small"
                        sx={{ fontWeight: 500, fontStyle: 'italic' }}
                      />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      px: '8px',
                      maxWidth: '400px',
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Global image variables:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    {Object.values(EPromptsGlobalImageVariables).map((variable) => (
                      <TextTag
                        key={variable}
                        label={variable}
                        bgColor={theme.palette.custom.lightGreen}
                        color={theme.palette.text.secondary}
                        fontSize="12px"
                        size="small"
                        sx={{ fontWeight: 500, fontStyle: 'italic' }}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '25px',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setValue(text)
                setIsEditing(false)
              }}
              disabled={isLoading}
            >
              {t('cancel-button-text.v')}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={isLoading || value === text}
            >
              {t('save-button-text.v')}
            </Button>
          </Box>
        </Box>
      </ModalWrapper>
    </>
  )
}
